import {useQuery} from "@tanstack/react-query";
import {driversMapPreview, reverseGeocode} from "@/api/apiClient";
import {useConfiguration} from "@/features/configuration";
import type {LocationWithCoordinates} from "@/features/location-suggestions/utils/types";
import {Map} from "@/features/map";
import type {LatLng} from "@/features/map/utils/types";
import {useOrderContext} from "@/features/order/context/OrderContext";
import {useFlowNavigation} from "@/features/order/hooks/useFlowNavigation";
import {logGtm} from "@/lib/gtm";

type OrderMapProps = {
    defaultCenter: LatLng;
    sheetOffset: {
        height: number;
        width: number;
    };
    isConfirmation?: boolean;
};

export function OrderMap({defaultCenter, sheetOffset, isConfirmation}: OrderMapProps) {
    const {data: configuration} = useConfiguration();
    const {orderDraft, overview, updateOrderDraft} = useOrderContext();
    const {handleLocationSelected} = useFlowNavigation();
    const pickup = orderDraft.pickup?.location;
    const dropoff = orderDraft.dropoff?.location;
    const rideShape = overview?.tiers?.[0]?.rideShape;

    const {data: drivers} = useQuery({
        queryKey: ["drivers"],
        queryFn: () =>
            driversMapPreview(pickup?.lat ?? defaultCenter.lat, pickup?.lon ?? defaultCenter.lng),
        enabled: !!pickup || !!defaultCenter,
        select: (data) => data.drivers,
        refetchInterval: 10000,
    });

    const handleSetDropoff = async (coordinates: LatLng) => {
        const location = await reverseGeocode({
            coordinates: {lat: coordinates.lat, lon: coordinates.lng},
        });

        logGtm.public.selectDropoff("map");

        updateOrderDraft({
            dropoff: {
                location: location as LocationWithCoordinates,
                complete: true,
                type: "SEARCH" as const,
            },
        });
        handleLocationSelected("dropoff");
    };

    const handleSetPickup = async (coordinates: LatLng) => {
        const location = await reverseGeocode({
            coordinates: {lat: coordinates.lat, lon: coordinates.lng},
        });

        logGtm.public.selectPickup("map");

        updateOrderDraft({
            pickup: {
                location: location as LocationWithCoordinates,
                complete: true,
                type: "SEARCH" as const,
            },
        });
        handleLocationSelected("pickup");
    };

    return (
        <Map
            onSetDropoff={handleSetDropoff}
            onSetPickup={handleSetPickup}
            defaultCenter={defaultCenter}
            rideShape={rideShape}
            sheetOffset={sheetOffset}
            isConfirmation={isConfirmation}
            isFixedPickup={!!configuration?.fixedPickup}
            isFixedDestination={!!configuration?.fixedDestination}
            pickup={pickup}
            dropoff={dropoff}
            drivers={drivers}
        />
    );
}
